<!--
  The draggable divider between the tree and the list. TASK-042 §4.

  Two things about it are deliberate:

  1. **Pointer events with `setPointerCapture`, not the HTML5 drag API.** A
     resize has no drag image and no drop target, and pointer capture is what
     keeps the drag alive when the pointer leaves this 6px strip and travels
     across a scrolling list.
  2. **It is fully operable from the keyboard.** A mouse-only resize, on a
     screen whose entire purpose is reading long folder names, is a feature
     half the audience cannot use. Left and Right move 16px, with Shift 64px,
     Home and End go to the limits, Enter toggles between the default width and
     wherever it was last dragged to. Double-click resets, because that is
     where people look and it is cheaper than a menu item.

  The width is COMMITTED on `pointerup` and `keyup`, never per frame: the owner
  writes it to `localStorage`, and a write per pointermove is a write per frame.
-->
<script lang="ts">
  let {
    width,
    min,
    max,
    defaultWidth,
    onwidth,
    oncommit,
  }: {
    width: number
    min: number
    max: number
    defaultWidth: number
    /** Called continuously during a drag. Cheap: it sets one custom property. */
    onwidth: (px: number) => void
    /** Called once a gesture ends. This is what persists. */
    oncommit: () => void
  } = $props()

  let el = $state<HTMLElement | null>(null)
  let dragging = $state(false)
  let startX = 0
  let startWidth = 0
  /**
   * Where Enter returns to, so the toggle is a toggle and not a one-way trip.
   * Not `$state`: nothing renders it, it is only read inside the handlers.
   */
  let lastCustom: number | null = null

  const KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'])

  function clamp(n: number): number {
    return Math.min(Math.max(Math.round(n), min), max)
  }

  function down(event: PointerEvent) {
    if (event.button !== 0) return
    dragging = true
    startX = event.clientX
    startWidth = width
    el?.setPointerCapture(event.pointerId)
    // Or the pointer selects the text of both panes on the way past.
    event.preventDefault()
  }

  function move(event: PointerEvent) {
    if (!dragging) return
    onwidth(clamp(startWidth + (event.clientX - startX)))
  }

  function up(event: PointerEvent) {
    if (!dragging) return
    dragging = false
    try {
      el?.releasePointerCapture(event.pointerId)
    } catch {
      /* the capture is already gone; there is nothing to release */
    }
    if (width !== defaultWidth) lastCustom = width
    oncommit()
  }

  function keydown(event: KeyboardEvent) {
    if (!KEYS.has(event.key)) return
    const step = event.shiftKey ? 64 : 16
    let next: number
    if (event.key === 'ArrowLeft') next = width - step
    else if (event.key === 'ArrowRight') next = width + step
    else if (event.key === 'Home') next = min
    else if (event.key === 'End') next = max
    else next = width === defaultWidth ? (lastCustom ?? min) : defaultWidth
    if (event.key !== 'Enter' && next !== defaultWidth) lastCustom = clamp(next)
    event.preventDefault()
    onwidth(clamp(next))
  }

  function keyup(event: KeyboardEvent) {
    if (KEYS.has(event.key)) oncommit()
  }

  function reset() {
    onwidth(defaultWidth)
    oncommit()
  }
</script>

<!--
  A focusable `separator` IS a widget in ARIA -- the window-splitter pattern
  the spec gives `aria-valuenow` and the arrow keys for -- so the tabindex and
  the handlers below are the point of this component rather than a mistake in
  it. Svelte's rule treats every separator as the static kind.
-->
<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
  bind:this={el}
  class="divider"
  class:dragging
  role="separator"
  tabindex="0"
  aria-orientation="vertical"
  aria-label="Resize the folder tree"
  aria-valuenow={width}
  aria-valuemin={min}
  aria-valuemax={max}
  onpointerdown={down}
  onpointermove={move}
  onpointerup={up}
  onpointercancel={up}
  onkeydown={keydown}
  onkeyup={keyup}
  ondblclick={reset}
>
  <span class="grip" aria-hidden="true"></span>
</div>

<style>
  .divider {
    align-self: stretch;
    width: 0.75rem;
    min-height: 6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: col-resize;
    /* The pointer owns this element for the duration of a drag; letting the
       browser also interpret the gesture as a scroll fights it. */
    touch-action: none;
  }

  .grip {
    width: 2px;
    align-self: stretch;
    border-radius: 1px;
    background: var(--color-line, #211d1824);
  }

  .divider:hover .grip,
  .divider:focus-visible .grip,
  .divider.dragging .grip {
    background: var(--color-ink-soft, #6e665c);
  }

  .divider:focus-visible {
    outline: 2px solid var(--color-ink, #211d18);
    outline-offset: -2px;
    border-radius: 0.25rem;
  }
</style>
